import { getCustomerCities } from '@src/services/reports';
import { TReportPaymentsByCustomerCityOption } from '@src/types/reports';
import { useCallback, useEffect, useRef, useState } from 'react';

export const useCustomerCities = () => {
  const [cityOptions, setCityOptions] = useState<
    TReportPaymentsByCustomerCityOption[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const requestIdRef = useRef(0);

  const perPage = 50;

  const fetchCities = useCallback(
    async (currentPage: number, currentSearch: string, isReset = false) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);

      try {
        const response = await getCustomerCities({
          search: currentSearch || undefined,
          page: currentPage,
          per_page: perPage,
        });

        if (requestId !== requestIdRef.current) {
          return;
        }

        const options = response.data;

        setCityOptions((prev) => {
          if (isReset) {
            return options;
          }

          const existingValues = new Set(prev.map((opt) => opt.value));
          const nextOptions = options.filter(
            (opt) => !existingValues.has(opt.value)
          );

          return [...prev, ...nextOptions];
        });

        setHasMore(response.has_next);
        setPage(response.current_page);
      } catch (error) {
        console.error('Error fetching customer cities:', error);
        if (isReset) {
          setCityOptions([]);
        }
        setHasMore(false);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    []
  );

  const searchCities = useCallback(
    (nextSearch: string) => {
      const normalizedSearch = nextSearch.trim();
      requestIdRef.current += 1;
      setSearch(normalizedSearch);
      setPage(1);
      setHasMore(true);
      fetchCities(1, normalizedSearch, true);
    },
    [fetchCities]
  );

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = page + 1;

      fetchCities(nextPage, search, false);
    }
  }, [fetchCities, hasMore, loading, page, search]);

  useEffect(() => {
    fetchCities(1, '', true);
  }, [fetchCities]);

  return { cityOptions, loading, hasMore, loadMore, searchCities };
};
